import React, { useState, useEffect } from "react";
import { Navbar, Nav, Form, FormControl, Offcanvas } from "react-bootstrap";
import { NavLink, useNavigate } from "react-router-dom";
import {
  FaShoppingCart,
  FaUser,
  FaSearch,
  FaChevronDown,
  FaArrowRight,
  FaSignOutAlt, // 👇 Added Logout Icon
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import "./header.css";

const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

const Header = () => {
  const [open, setOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // 👇 New state to check if user is logged in
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const navigate = useNavigate();

  // 👇 Check for token when component mounts
  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsLoggedIn(!!token);
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);

        const res = await fetch(`${REACT_APP_API_URL}/products/categories`);

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data = await res.json();

        setCategories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch categories:", err);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();

    const query = searchQuery.trim();

    if (query) {
      setOpen(false);
      setMobileMenuOpen(false);

      navigate(`/shop?search=${encodeURIComponent(query)}`);
      setSearchQuery("");
    }
  };

  // 👇 Logout Handler
  const handleLogout = () => {
    localStorage.removeItem("token"); // Remove token
    setIsLoggedIn(false); // Update state
    closeMobileMenu();
    navigate("/"); // Redirect to home
    window.location.reload(); // Refresh to clear user data
  };

  const getCategoryUrl = (category) => {
    return `/shop?category=${encodeURIComponent(category)}`;
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setOpen(false);
  };

  const handleCategoryToggle = () => {
    setOpen((previous) => !previous);
  };

  const renderNavigation = () => (
    <Nav className="premium-nav align-items-xl-center">
      <NavLink
        to="/aboutus"
        className="premium-nav-link"
        onClick={closeMobileMenu}
      >
        About Us
      </NavLink>

      <div
        className={`premium-shop-dropdown ${open ? "dropdown-open" : ""}`}
        onMouseEnter={() => {
          if (window.innerWidth >= 1200) setOpen(true);
        }}
        onMouseLeave={() => {
          if (window.innerWidth >= 1200) setOpen(false);
        }}
      >
        <div className="shop-nav-row">
          <NavLink
            to="/shop"
            className="premium-nav-link shop-main-link"
            onClick={closeMobileMenu}
          >
            Shop
          </NavLink>

          <button
            type="button"
            className="category-toggle"
            aria-label="Toggle shop categories"
            aria-expanded={open}
            onClick={handleCategoryToggle}
          >
            <FaChevronDown />
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              className="premium-dropdown-menu"
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <div className="dropdown-heading">
                <span>EXPLORE COLLECTIONS</span>
              </div>

              {loading ? (
                <div className="dropdown-status">
                  <span className="category-loader" />
                  Loading categories...
                </div>
              ) : categories.length > 0 ? (
                categories.map((category, index) => (
                  <motion.div
                    key={category}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.2,
                      delay: index * 0.035,
                    }}
                  >
                    <NavLink
                      to={getCategoryUrl(category)}
                      className="premium-dropdown-item"
                      onClick={closeMobileMenu}
                    >
                      <span>{category}</span>
                      <FaArrowRight className="dropdown-arrow" />
                    </NavLink>
                  </motion.div>
                ))
              ) : (
                <div className="dropdown-status">No categories available.</div>
              )}

              <NavLink
                to="/shop"
                className="premium-view-all"
                onClick={closeMobileMenu}
              >
                View All Products
                <FaArrowRight />
              </NavLink>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <NavLink
        to="/brands"
        className="premium-nav-link"
        onClick={closeMobileMenu}
      >
        Brands
      </NavLink>

      <NavLink
        to="/contactus"
        className="premium-nav-link"
        onClick={closeMobileMenu}
      >
        Contact
      </NavLink>
    </Nav>
  );

  return (
    <motion.header
      className="premium-header"
      initial={{ y: -75, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.65, ease: "easeOut" }}
    >
      <Navbar expand="xl" className="premium-navbar">
        <div className="premium-navbar-inner">
          {/* Logo */}
          <Navbar.Brand
            as={NavLink}
            to="/"
            className="premium-logo"
            onClick={closeMobileMenu}
          >
            <img
              src="/images/logo.png"
              alt="Crescent Store"
              className="premium-logo-img"
            />
          </Navbar.Brand>

          {/* Desktop Navigation */}
          <div className="desktop-navigation">{renderNavigation()}</div>

          {/* Desktop Search */}
          <Form
            className="premium-search desktop-search"
            onSubmit={handleSearch}
          >
            <FaSearch className="search-icon" />

            <FormControl
              type="search"
              name="search"
              placeholder="Search products..."
              aria-label="Search products"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <button type="submit" aria-label="Submit search">
              <FaArrowRight />
            </button>
          </Form>

          {/* Header Actions */}
          <div className="premium-actions">
            <motion.div
              whileHover={{ y: -3, scale: 1.06 }}
              whileTap={{ scale: 0.94 }}
            >
              <NavLink
                to="/cart"
                className="premium-action-link"
                aria-label="Shopping cart"
              >
                <FaShoppingCart />
              </NavLink>
            </motion.div>

            {/* 👇 Conditional Rendering for Login/Logout */}
            {isLoggedIn ? (
              <motion.div
                whileHover={{ y: -3, scale: 1.06 }}
                whileTap={{ scale: 0.94 }}
              >
                <button
                  onClick={handleLogout}
                  className="premium-action-link"
                  aria-label="Logout"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  <FaSignOutAlt />
                </button>
              </motion.div>
            ) : (
              <motion.div
                whileHover={{ y: -3, scale: 1.06 }}
                whileTap={{ scale: 0.94 }}
              >
                <NavLink
                  to="/login"
                  className="premium-action-link"
                  aria-label="My account"
                >
                  <FaUser />
                </NavLink>
              </motion.div>
            )}

            {/* Mobile Menu Toggle */}
            <button
              type="button"
              className={`premium-menu-toggle ${
                mobileMenuOpen ? "menu-is-open" : ""
              }`}
              aria-label="Toggle navigation menu"
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen((previous) => !previous)}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>

        {/* Mobile / Tablet Offcanvas */}
        <Offcanvas
          show={mobileMenuOpen}
          onHide={closeMobileMenu}
          placement="end"
          className="premium-offcanvas"
          backdrop
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>
              <img
                src="/images/logo.png"
                alt="Crescent Store"
                className="offcanvas-logo"
              />
            </Offcanvas.Title>
          </Offcanvas.Header>

          <Offcanvas.Body>
            <Form
              className="premium-search mobile-search"
              onSubmit={handleSearch}
            >
              <FaSearch className="search-icon" />

              <FormControl
                type="search"
                name="mobile-search"
                placeholder="Search products..."
                aria-label="Search products"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

              <button type="submit" aria-label="Submit search">
                <FaArrowRight />
              </button>
            </Form>

            {renderNavigation()}

            <div className="mobile-menu-footer">
              <span>Discover something special.</span>

              <div className="mobile-footer-links">
                <NavLink to="/cart" onClick={closeMobileMenu}>
                  <FaShoppingCart />
                  My Cart
                </NavLink>

                {/* 👇 Mobile Conditional Rendering */}
                {isLoggedIn ? (
                  <button 
                    onClick={handleLogout} 
                    style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '8px', color: 'inherit', padding: 0, cursor: 'pointer' }}
                  >
                    <FaSignOutAlt />
                    Logout
                  </button>
                ) : (
                  <NavLink to="/login" onClick={closeMobileMenu}>
                    <FaUser />
                    My Account
                  </NavLink>
                )}
              </div>
            </div>
          </Offcanvas.Body>
        </Offcanvas>
      </Navbar>
    </motion.header>
  );
};

export default Header;